import React, { useState, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useUpdateRepairPolicy } from "./hooks/useUpdateRepairPolicy";
import { toast } from "react-toastify";
import { Row, Col, Button, Form } from "react-bootstrap";
import LoadingInput from "components/common/LoadingInput/LoadingInput";

const styles = {
  btnWrapper: {
    flex: "0 0 10%",
  },
};

const EditableTextItem = React.memo(({ detail, lg = false }) => {
  const [editState, setEditState] = useState({
    isEditing: false,
    currentText: detail.displayText,
    originalText: detail.displayText,
  });

  const { mutate: updateRepairPolicyMutation, isPendingUpdate: isUpdating } =
    useUpdateRepairPolicy();

  const handleSuccess = useCallback(() => {
    toast.success("Successfully updated the policy.");
    setEditState((prev) => ({
      ...prev,
      isEditing: false,
      originalText: prev.currentText,
    }));
  }, []);

  const handleError = useCallback((errMsg) => {
    toast.error(errMsg || "Failed to update policy");
  }, []);

  const handleUpdate = useCallback(() => {
    if (!editState.currentText.trim()) {
      toast.error("Text cannot be empty");
      return;
    }

    updateRepairPolicyMutation(
      {
        id: detail._id,
        payload: { displayText: editState.currentText },
      },
      {
        onSuccess: handleSuccess,
        onError: (error) => handleError(error.message),
      }
    );
  }, [
    detail._id,
    editState.currentText,
    updateRepairPolicyMutation,
    handleSuccess,
    handleError,
  ]);

  const handleCancel = useCallback(() => {
    setEditState((prev) => ({
      ...prev,
      isEditing: false,
      currentText: prev.originalText,
    }));
  }, []);

  const handleEdit = useCallback(() => {
    setEditState((prev) => ({
      ...prev,
      isEditing: true,
    }));
  }, []);

  const handleTextChange = useCallback((e) => {
    setEditState((prev) => ({
      ...prev,
      currentText: e.target.value,
    }));
  }, []);

  return (
    <Row>
      <Col lg={lg ? null : 5}>
        <Form.Group>
          <LoadingInput
            as="textarea"
            rows={5}
            onChange={handleTextChange}
            value={editState.currentText}
            disabled={!editState.isEditing || isUpdating}
            isLoading={isUpdating}
            style={{ marginBottom: "15px" }}
          />
        </Form.Group>
      </Col>

      <Col style={styles.btnWrapper}>
        {editState.isEditing ? (
          <Button
            className="__btn_expanded"
            disabled={isUpdating}
            variant="outline-success"
            onClick={handleUpdate}
          >
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        ) : (
          <Button
            className="__btn_expanded"
            variant="outline-secondary"
            disabled={isUpdating}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </Col>
      <Col style={styles.btnWrapper}>
        {editState.isEditing && (
          <Button
            className="__btn_expanded"
            variant="danger"
            disabled={isUpdating}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Col>
    </Row>
  );
});

const SectionTitle = memo(({ title }) => (
  <Row style={{ marginTop: "1em" }}>
    <Col>
      <Form.Label style={{ fontWeight: "bold", fontSize: "16px" }}>{title}</Form.Label>
    </Col>
    <Col style={styles.btnWrapper} />
  </Row>
));

const DisplayData = memo(({ data, lg = true }) => {
  if (!data?.length) {
    return null;
  }

  return (
    <>
      {data.map((info) => (
        <React.Fragment key={info._id}>
          <SectionTitle title={info.label} />
          <EditableTextItem detail={info} lg={lg} />
        </React.Fragment>
      ))}
    </>
  );
});

export default DisplayData;
